import { useListIngestIntakesQuery } from '@axilon-apps/domains-ontology-ui-state'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../../components/ui/alert-dialog'
import { usedByCell, type UsedByEntry } from '../../IngestPage/configs/config-view'

/**
 * Archive is per version row, so archiving the config means every active
 * version, newest first — otherwise the picker would fall back to the
 * previous one. Archiving leaves intakes as they are, so the dialog names the
 * ones still pointing at the config: their new files fail until each one is
 * repointed or turned off.
 */
export function ArchiveConfigDialog({
  open,
  onOpenChange,
  name,
  activeVersions,
  loading,
  error,
  onConfirm
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  name: string
  activeVersions: number
  loading: boolean
  error?: string
  onConfirm: () => void
}) {
  const intakesQuery = useListIngestIntakesQuery(undefined, { skip: !open })
  const intakes = intakesQuery.data?.intakes
  const usedBy = intakes ? usedByCell(name, intakes) : undefined

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This archives all {activeVersions} version{activeVersions === 1 ? '' : 's'} and hides
            the config from every picker. Recorded runs are kept: uploads in <em>Runs</em>, intake
            files on their intake. The DuckDB connection{' '}
            <code className="text-11">urn:db:{name}</code> and every row written to it are kept.
          </AlertDialogDescription>
          <IntakeWarning usedBy={usedBy} unknown={intakesQuery.isError} />
        </AlertDialogHeader>
        {error ? (
          <p role="alert" className="text-sm text-st-down">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Keep</AlertDialogCancel>
          <AlertDialogAction
            variant="outline"
            className="border-st-down bg-st-down text-white shadow-sm hover:bg-st-down/90 hover:text-white dark:border-st-down dark:bg-st-down dark:text-white dark:hover:bg-st-down/90 dark:hover:text-white"
            disabled={loading}
            onClick={(event) => {
              event.preventDefault()
              onConfirm()
            }}
          >
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function IntakeWarning({ usedBy, unknown }: { usedBy?: UsedByEntry[]; unknown: boolean }) {
  if (unknown) {
    return (
      <p className="text-sm text-st-warn">
        Archiving does not change intakes. Any intake that points at this config fails its new files
        until it points at another config or is turned off.
      </p>
    )
  }
  if (!usedBy?.length) return null
  return (
    <div className="text-sm text-st-warn" data-testid="archive-intake-warning">
      <p>
        {usedBy.length === 1 ? '1 intake still points' : `${usedBy.length} intakes still point`} at
        it. Archiving does not change {usedBy.length === 1 ? 'it' : 'them'}: new files fail until
        each one points at another config or is turned off.
      </p>
      <ul className="mt-1 list-disc pl-5">
        {usedBy.map((entry) => (
          <li key={entry.intakeId}>
            <span className="font-medium">{entry.intakeName}</span>
            {entry.pinnedVersion ? (
              <span className="text-ink-3"> (pinned to v{entry.pinnedVersion})</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
